/**
 * Page geometry for the printables family (certificates, name tags, calendars, staff
 * paper and the rest).
 *
 * Every printable is an SVG whose `viewBox` is the sheet in millimetres and whose `width`
 * and `height` are the sheet in real units, so one user unit prints as one millimetre.
 * `@page` has no margin: the browser then adds no header or footer, and the margin
 * stated to the reader is drawn into the sheet instead (0.5 in on Letter, 12 mm on A4),
 * which is also where every layout keeps its content.
 */

export type PaperSize = "letter" | "a4";
export type Orientation = "portrait" | "landscape";

/** Millimetres per inch and per typographic point. */
export const MM_PER_INCH = 25.4;
export const MM_PER_POINT = 25.4 / 72;

/** The margin each paper keeps clear: 0.5 in on Letter, 12 mm on A4. */
export const PAGE_MARGIN_MM: Record<PaperSize, number> = {
  letter: 12.7,
  a4: 12,
};

const PAPER_MM: Record<PaperSize, { width: number; height: number }> = {
  letter: { width: 215.9, height: 279.4 },
  a4: { width: 210, height: 297 },
};

export interface PageGeometry {
  paper: PaperSize;
  orientation: Orientation;
  /** Sheet size in millimetres (user units). */
  width: number;
  height: number;
  /** The sheet's real size as CSS lengths. */
  cssWidth: string;
  cssHeight: string;
  /** The clear margin, and the content box inside it. */
  margin: number;
  left: number;
  top: number;
  contentWidth: number;
  contentHeight: number;
}

/** A sheet of `paper` with the family's standard margin (or a custom one). */
export function pageGeometry(
  paper: PaperSize,
  orientation: Orientation,
  margin: number = PAGE_MARGIN_MM[paper]
): PageGeometry {
  const base = PAPER_MM[paper];
  const landscape = orientation === "landscape";
  const width = landscape ? base.height : base.width;
  const height = landscape ? base.width : base.height;
  let cssWidth = `${width}mm`;
  let cssHeight = `${height}mm`;
  if (paper === "letter") {
    cssWidth = landscape ? "11in" : "8.5in";
    cssHeight = landscape ? "8.5in" : "11in";
  }
  return {
    paper,
    orientation,
    width,
    height,
    cssWidth,
    cssHeight,
    margin,
    left: margin,
    top: margin,
    contentWidth: width - margin * 2,
    contentHeight: height - margin * 2,
  };
}

/** Class every printable page carries; the print CSS keys on it. */
export const PAGE_CLASS = "pr-page";

/**
 * Print CSS: the sheet's real size, no browser margin, one SVG per physical page.
 *
 * The height is 1 mm short of the sheet because a page that is exactly as tall as the
 * paper makes some browsers add a blank trailing page.
 */
export function printablePageCss(geometry: PageGeometry): string {
  return [
    `@page { size: ${geometry.cssWidth} ${geometry.cssHeight}; margin: 0; }`,
    "@media print {",
    "  [data-tool-page] > div { max-width: none !important; }",
    `  .${PAGE_CLASS} { display: block !important; width: ${geometry.cssWidth} !important; height: calc(${geometry.cssHeight} - 1mm) !important; max-width: none !important; margin: 0 !important; border: 0 !important; border-radius: 0 !important; box-shadow: none !important; break-after: page; break-inside: avoid; }`,
    `  .${PAGE_CLASS}:last-of-type { break-after: auto; }`,
    "}",
  ].join("\n");
}

/** Rounds a coordinate to 0.01 mm, so server and browser trigonometry print the same. */
export function r2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Rounds to 0.1 mm — for anything derived from sin and cos. */
export function r1(value: number): number {
  return Math.round(value * 10) / 10;
}

/** Splits a list into rows of `size` (the last may be short). */
export function chunkBy<T>(items: readonly T[], size: number): T[][] {
  const step = Math.max(1, Math.floor(size));
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += step) {
    rows.push(items.slice(i, i + step));
  }
  return rows;
}

const LINE_BREAK = /\r?\n/;

/** Non-empty trimmed lines of a pasted list. */
export function parseLines(raw: string): string[] {
  return raw
    .split(LINE_BREAK)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}
